import React, { useCallback, useMemo, useState } from 'react';
import { matchesWorkspace } from '../utils/accountSearch';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { useHorizontalScrollEdges } from '../hooks/useScrollEdges';
import { formatDate } from '../utils/formatDate';
import { useNavigate } from 'react-router-dom';
import { useAuth, PipelineStage, Workspace } from '../contexts/AuthContext';
import { errorMessageOf } from '../utils/apiErrors';
import { useRole } from '../hooks/useRole';
import { useBoardDrag, type BoardMove } from '../hooks/useBoardDrag';
import { makeMoney, CURRENCY_NOT_SET } from '../utils/currency';

import Icon from './Icon';
import AccountBar from './account/AccountBar';
import AccountMetrics from './account/AccountMetrics';
import { pipelineMetrics } from './account/accountMetricSets';
import { useExport } from './header/exporters';
import BreadcrumbBar, { type BreadcrumbAction } from './BreadcrumbBar';
import { useNotifications } from './NotificationProvider';
import { NEW_LEAD_PATH } from '../utils/accountRoutes';
import '../styles/components/PipelineView.css';

/** A card's contract value in the project's own currency; never guessed, never summed across projects. */
export function contractValueText(card: Pick<Workspace, 'contract_value' | 'contract_currency'>): string | null {
  if (card.contract_value === null) return null;
  return card.contract_currency ? makeMoney(card.contract_currency).format(Number(card.contract_value)) : CURRENCY_NOT_SET;
}

const STAGES: { value: PipelineStage; label: string }[] = [
  { value: 'lead', label: 'Lead' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'booked', label: 'Booked' },
  { value: 'delivering', label: 'Delivering' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const DRAG_HELP_ID = 'pl-drag-help';

const formatCardDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatDate(d);
};

const PipelineView: React.FC = () => {
  const { workspaces, updatePipelineStage } = useAuth();
  // Contractors and anyone flagged never see money; the card then shows no value.
  const { canViewFinancials, canEdit } = useRole();
  const navigate = useNavigate();
  const { showError } = useNotifications();
  // Projects with a save in flight, and the stage each is shown in until the server has answered (optimistic).
  const [savingIds, setSavingIds] = useState<ReadonlySet<string>>(() => new Set());
  const [shownStage, setShownStage] = useState<Readonly<Record<string, PipelineStage>>>({});
  // The six stage columns are wider than a phone: fades on the side that still has stages say so.
  const { ref: boardRef, edges } = useHorizontalScrollEdges<HTMLDivElement>();

  const stageOf = useCallback((w: Workspace): PipelineStage => shownStage[w.project_id] ?? w.pipeline_stage, [shownStage]);

  // The one place a card changes stage: the arrow buttons and all three drag paths come here.
  // The card moves at once; if the server refuses, it goes back and the person is told why.
  const moveCard = useCallback(async (move: Pick<BoardMove, 'itemId' | 'label' | 'to'> & { via?: BoardMove['via'] }) => {
    const { itemId: projectId, label, to } = move;
    const stage = to as PipelineStage;
    if (!canEdit || savingIds.has(projectId)) return;
    const current = workspaces.find(w => w.project_id === projectId);
    if (!current || stageOf(current) === stage) return;
    setSavingIds(prev => new Set(prev).add(projectId));
    setShownStage(prev => ({ ...prev, [projectId]: stage }));
    try {
      await updatePipelineStage(projectId, stage);
    } catch (err) {
      showError(errorMessageOf(err, 'Could not move this card.'));
      drag.announce(`Could not move ${label}. It is back in ${STAGES.find(x => x.value === current.pipeline_stage)?.label}.`);
      if (move.via === 'keyboard') drag.restoreFocus(projectId);
    } finally {
      setShownStage(prev => Object.fromEntries(Object.entries(prev).filter(([id]) => id !== projectId)));
      setSavingIds(prev => new Set(Array.from(prev).filter(id => id !== projectId)));
    }
  }, [canEdit, savingIds, workspaces, stageOf, updatePipelineStage, showError]);

  const columns = useMemo(() => STAGES.map(s => ({ id: s.value, label: s.label })), []);
  const drag = useBoardDrag({
    columns,
    onMove: moveCard,
    disabled: !canEdit,
    lockedItemIds: savingIds,
    containerRef: boardRef,
  });

  // The header search narrows the board.
  const search = useHeaderSearch();
  const byStage = (stage: PipelineStage): Workspace[] =>
    workspaces.filter(w => stageOf(w) === stage && matchesWorkspace(w, search));

  // Export button: the projects on the board right now; contract value only for a role that may see money.
  const onBoard = workspaces.filter(w => matchesWorkspace(w, search));
  useExport('account-projects', { rows: onBoard, ctx: { showMoney: canViewFinancials } });

  const barActions: BreadcrumbAction[] = canEdit
    ? [{ kind: 'primary', key: 'new', icon: 'plus', label: 'New lead', onClick: () => navigate(NEW_LEAD_PATH) }]
    : [];

  return (
    <div className="wt-container fade-in">
        <AccountBar
          view="pipeline"
          actions={<BreadcrumbBar actions={barActions} />}
          exportKey="account-projects"
        />
        {workspaces.length > 0 && (
          <AccountMetrics label="Pipeline summary" title="These figures follow the search box at the top" metrics={pipelineMetrics(onBoard, canViewFinancials)} />
        )}

        <div className="pl-board">
          <div className={`pl-board-fade pl-board-fade--left${edges.left ? ' is-visible' : ''}`} aria-hidden="true" />
          <div
            ref={boardRef}
            className="pl-columns"
            role="region"
            aria-label="Pipeline stages. Scroll sideways to see every stage."
            tabIndex={0}
          >
          {STAGES.map((stage, colIdx) => {
            const cards = byStage(stage.value);
            const over = drag.isOver(stage.value);
            return (
              <div
                className={`pl-column${over ? ' is-drop-target' : ''}`}
                key={stage.value}
                role="group"
                aria-label={`${stage.label}, ${cards.length} ${cards.length === 1 ? 'project' : 'projects'}`}
                {...drag.getColumnProps(stage.value)}
              >
                <div className="pl-column-header">
                  <span className="pl-column-label">{stage.label}</span>
                  <span className="pl-column-count">{cards.length}</span>
                </div>
                <div className="pl-column-body">
                  {cards.length === 0 && <div className="pl-empty">{over ? 'Drop here' : 'No projects'}</div>}
                  {cards.map(card => {
                    const saving = savingIds.has(card.project_id);
                    const lifted = drag.isDragging(card.project_id);
                    const itemProps = drag.getItemProps({ id: card.project_id, columnId: stage.value, label: card.project_name });
                    return (
                    <div
                      className={`pl-card${canEdit ? ' is-draggable' : ''}${lifted ? ' is-lifted' : ''}${saving ? ' is-saving' : ''}`}
                      key={card.project_id}
                      role="group"
                      aria-label={`${card.project_name}, ${stage.label}`}
                      aria-roledescription={canEdit ? 'draggable card' : undefined}
                      aria-describedby={canEdit ? DRAG_HELP_ID : undefined}
                      aria-busy={saving || undefined}
                      tabIndex={canEdit ? 0 : undefined}
                      {...itemProps}
                    >
                      <div className="pl-card-name">{card.project_name}</div>
                      {formatCardDate(card.project_date) && (
                        <div className="pl-card-date">{formatCardDate(card.project_date)}</div>
                      )}
                      {canViewFinancials && contractValueText(card) && (
                        <div className="pl-card-value">{contractValueText(card)}</div>
                      )}
                      <div className="pl-card-actions">
                        <button
                          type="button"
                          className="pl-card-arrow"
                          disabled={!canEdit || colIdx === 0 || saving}
                          title={`Move to ${STAGES[colIdx - 1]?.label ?? ''}`}
                          aria-label={`Move ${card.project_name} to ${STAGES[colIdx - 1]?.label ?? ''}`}
                          onClick={() => moveCard({ itemId: card.project_id, label: card.project_name, to: STAGES[colIdx - 1].value })}
                        >
                          <Icon name="chevronLeft" size={14} />
                        </button>
                        <button
                          type="button"
                          className="pl-card-arrow"
                          disabled={!canEdit || colIdx === STAGES.length - 1 || saving}
                          title={`Move to ${STAGES[colIdx + 1]?.label ?? ''}`}
                          aria-label={`Move ${card.project_name} to ${STAGES[colIdx + 1]?.label ?? ''}`}
                          onClick={() => moveCard({ itemId: card.project_id, label: card.project_name, to: STAGES[colIdx + 1].value })}
                        >
                          <Icon name="chevronRight" size={14} />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          </div>
          <div className={`pl-board-fade pl-board-fade--right${edges.right ? ' is-visible' : ''}`} aria-hidden="true" />
        </div>

        {canEdit && (
          <p id={DRAG_HELP_ID} className="sr-only">
            Drag a card to another stage. With the keyboard, press Space to pick a card up, the left and right arrow keys to choose a stage, Space to drop it, or Escape to cancel.
          </p>
        )}
        <div className="sr-only" aria-live="polite" aria-atomic="true">{drag.announcement}</div>
    </div>
  );
};

export default PipelineView;
